import React, { FC, useMemo, useState } from "react";
import Icon from "../Icon/index";
import { Trans, t } from "@helpers/translate";
import { FACEBOOK_APP_ID } from "@constants";
import { generateXTwitterShareLink } from "@helpers/share";
import { useNotification } from "@helpers/hooks/useNotification";
import useIsMobile from "@helpers/useIsMobile";

interface ITourProps {
  type: "tour";
  title: string;
  country: string;
  city: string;
  url: string;
}

interface IBlogProps {
  type: "blog";
  title: string;
  country: string;
  city: string;
  url: string;
  posPopCenter?: boolean;
}

interface ISupplierProps {
  type: "supplier" | "author";
  displayName: string;
  country: string;
  url: string;
}

export const ShareButton: FC<ITourProps | IBlogProps | ISupplierProps> = (
  payload
) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const notification = useNotification();
  const isMobile = useIsMobile();

  const mountEmailData = useMemo(() => {
    if (payload.type === "tour") {
      return {
        subject: `Check out this ${payload.city} tour on GoWithGuide`,
        body: `I found this ${
          payload.city
        } tour on GoWithGuide and thought you'd love it: ${payload.title.replace(
          /&/g,
          "and"
        )} Here is the link: ${payload.url}`,
      };
    }
    if (payload.type === "blog") {
      return {
        subject: `Check out this article about ${payload.country} on GoWithGuide`,
        body: `I found this article about ${payload.country} on GoWithGuide and thought you'd love it: ${payload.title} Here is the link: ${payload.url}`,
      };
    }

    if (payload.type === "supplier") {
      return {
        subject: `Check out this ${payload.country} guide from GoWithGuide`,
        body: `I found this ${payload.country} guide from GoWithGuide and thought you'd love it: ${payload.displayName} Here is the link: ${payload.url}`,
      };
    }

    if (payload.type === "author") {
      return {
        subject: `Check out this author ${payload.displayName} on GoWithGuide`,
        body: `I found this authors article on GoWithGuide and thought you'd love it. Here is the link: ${payload.url}`,
      };
    }

    return {
      subject: "",
      body: "",
    };
  }, []);

  const mountSmsData = useMemo(() => {
    if (payload.type === "tour") {
      return {
        subject: "",
        body: `I found this ${payload.city} tour on GoWithGuide and thought you'd love it: ${payload.title}. Here is the link: ${payload.url}`,
      };
    }
    if (payload.type === "blog") {
      return {
        subject: "",
        body: `I found this article about ${payload.country} on GoWithGuide and thought you'd love it: ${payload.title}. Here is the link: ${payload.url}`,
      };
    }
    if (payload.type === "supplier") {
      return {
        subject: "",
        body: `I found this supplier ${payload.displayName} on GoWithGuide and thought you'd love it. Here is the link: ${payload.url}`,
      };
    }

    if (payload.type === "author") {
      return {
        subject: "",
        body: `I found this author ${payload.displayName} on GoWithGuide and thought you'd love it. Here is the link: ${payload.url}`,
      };
    }

    return {
      subject: "",
      body: "",
    };
  }, []);

  //Message
  const message = useMemo(() => {
    let message = "";

    if (payload.type === "tour") {
      message = `I found this ${payload.city} tour on GoWithGuide and thought you'd love it: ${payload.title}. Here is the link: ${payload.url}`;
    }

    if (payload.type === "supplier") {
      message = `Check out this ${payload.country} guide I found on GoWithGuide: ${payload.displayName} Here is the link: ${payload.url}`;
    }

    if (payload.type === "author") {
      message = `Check out this author ${payload.displayName} on GoWithGuide. Here is the link: ${payload.url}`;
    }

    if (payload.type === "blog") {
      message = `Check out this article on @GoWithGuide: "${payload.title}" #gowithguide ${payload.url}`;
    }

    return message;
  }, [payload]);

  //Title
  const title = () => {
    let message = "";

    if (payload.type === "tour") {
      message = "Share this Tour";
    }

    if (payload.type === "supplier") {
      message = "Share this Supplier";
    }

    if (payload.type === "author") {
      message = "Share this Author";
    }

    return message;
  };

  // FaceBook
  const messengerLink = useMemo(() => {
    if (isMobile) {
      return `fb-messenger://share?link=${payload.url}`;
    }
    return `https://facebook.com/dialog/send?link=${payload.url}&app_id=${FACEBOOK_APP_ID}&redirect_uri=https://facebook.com`;
  }, [payload]);

  // FaceBook
  const facebookLink = useMemo(() => {
    return `https://www.facebook.com/sharer/sharer.php?u=${payload.url}`;
  }, [payload]);

  //  Twitter
  const xTwitterShareLink = useMemo(() => {
    return generateXTwitterShareLink(`${message}`);
  }, [payload, message]);

  return (
    <div
      className={`flex relative sharebtn bg-white rounded-xl z-10 border justify-center align-middle ${payload.type === "blog" ? "h-[30px] mt-[5px]" : ""}`}
    >
      <button
        className={`flex flex-row items-center px-4 pt-2 pb-2 rounded-xl border-0 hover:bg-share_button_hover ${payload.type === "blog" ? "h-4 py-0.5 mt-[5px] " : ""}`}
        style={{
          borderColor: "#E3E3E3",
        }}
        onClick={() => {
          setShowModal(!showModal);
        }}
      >
        <span className=" hidden sm:block">
          <Icon icon="share" size="sm" className="text-black" />
        </span>
        <span className=" block sm:hidden">
          <Icon icon="share" size="xs" className="text-black" />
        </span>

        <p className="text-black px-2 font-bold hidden lg:block ">Share</p>
      </button>

      {showModal && (
        <>
          {/* Full-screen grey overlay */}
          <div
            className="fixed inset-0 bg-black bg-black/40 z-40"
            onClick={() => setShowModal(false)}
          />
          <div
            onClick={() => setShowModal(false)}
            className={`
            z-50
            fixed inset-0 flex justify-center items-center        // Center for mobile
            ${payload.type === "blog" && payload.posPopCenter ? "" : "sm:absolute sm:top-full sm:mt-2 sm:right-0 sm:inset-auto sm:flex-none"}
            `}
          >
            <div className="p-2 bg-white rounded-xl border shadow-md w-80">
              <p className="font-semibold pl-4 my-2 text-center sm:text-left">
                {title()}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 m-4">
                {/* Copy Link */}
                <div>
                  <button
                    className="w-full"
                    onClick={() => {
                      setShowModal(false);
                      navigator.clipboard.writeText(payload.url);
                      setLinkCopied(true);

                      setTimeout(() => {
                        setLinkCopied(false);
                      }, 2000);
                      notification.success("Link Copied");
                    }}
                  >
                    <div
                      className={`flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border ${
                        linkCopied ? "text-green-600" : "text-gray-800"
                      }`}
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="copy"
                        size="sm"
                        className={`mr-2 ${
                          linkCopied ? "text-green-600" : "text-gray-600"
                        }`}
                      />
                      {linkCopied ? (
                        <Trans id="share.copied">Copied!</Trans>
                      ) : (
                        <Trans id="share.copy">Copy Link</Trans>
                      )}
                    </div>
                  </button>
                </div>
                {/* Email */}
                <div>
                  <a
                    title={t({
                      id: "common.link.title",
                      message:
                        "GoWithGuide - Private Tours & Local Tour Guides",
                    })}
                    onClick={() => {
                      setShowModal(false);
                    }}
                    href={`mailto:?subject=${mountEmailData.subject}&body=${mountEmailData.body}`}
                  >
                    <div
                      className="flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border"
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="email"
                        size="sm"
                        className="mr-2 text-gray-600"
                      />

                      <Trans id="share.email">Email</Trans>
                    </div>
                  </a>
                </div>

                {/*  Text Message */}
                <div className="md:hidden">
                  <a
                    title={t({
                      id: "common.link.title",
                      message:
                        "GoWithGuide - Private Tours & Local Tour Guides",
                    })}
                    onClick={() => {
                      setShowModal(false);
                    }}
                    href={`sms:?&body=${mountSmsData.body}`}
                  >
                    <div
                      className="flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border"
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="chat"
                        size="sm"
                        className="mr-2 text-gray-600"
                      />

                      <Trans id="share.text-message">Text Message</Trans>
                    </div>
                  </a>
                </div>

                {/* Facebook */}
                <div>
                  <a
                    title={t({
                      id: "common.link.title",
                      message:
                        "GoWithGuide - Private Tours & Local Tour Guides",
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={facebookLink}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  >
                    <div
                      className="flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border"
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="facebookRound"
                        size="sm"
                        className="mr-2 text-gray-600"
                      />

                      <Trans id="share.email">Facebook</Trans>
                    </div>
                  </a>
                </div>

                {/* X */}
                <div>
                  <a
                    title={t({
                      id: "common.link.title",
                      message:
                        "GoWithGuide - Private Tours & Local Tour Guides",
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={xTwitterShareLink}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  >
                    <div
                      className="flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border"
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="xtwitter"
                        size="sm"
                        className="mr-2 text-gray-600"
                      />

                      <Trans id="share.email">X</Trans>
                    </div>
                  </a>
                </div>

                {/* WhatsApp */}
                <div>
                  <a
                    title={t({
                      id: "common.link.title",
                      message:
                        "GoWithGuide - Private Tours & Local Tour Guides",
                    })}
                    onClick={() => {
                      setShowModal(false);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://wa.me/?text=${message}`}
                  >
                    <div
                      className="flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border"
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="whatsapp"
                        size="sm"
                        className="mr-2 text-gray-600"
                      />

                      <Trans id="share.email">WhatsApp</Trans>
                    </div>
                  </a>
                </div>

                {/* Messenger */}
                <div>
                  <a
                    title={t({
                      id: "common.link.title",
                      message:
                        "GoWithGuide - Private Tours & Local Tour Guides",
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={messengerLink}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  >
                    <div
                      className="flex flex-row items-center whitespace-nowrap px-2 py-2 hover:bg-gray-100 rounded-xl hover:shadow-md border"
                      style={{
                        borderColor: "#E3E3E3",
                      }}
                    >
                      <Icon
                        icon="messenger"
                        size="sm"
                        className="mr-2 text-gray-600"
                      />

                      <Trans id="share.email">Messenger</Trans>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
